// dataQuality.ts — każda liczba pokazana operatorowi niesie ze sobą informację, ile jest warta.
//
// Powód: dashboard, który pokazuje „−21%” bez informacji, że policzono to z 6 obserwacji,
// produkuje złe decyzje szybciej niż brak dashboardu.

export type DataQuality =
  | 'GOOD'
  | 'PARTIAL'
  | 'LOW_SAMPLE'
  | 'MISSING_HISTORY'
  | 'UNRELIABLE_JOIN'
  | 'UNKNOWN';

export interface DataQualityAssessment {
  quality: DataQuality;
  /** Zdanie po polsku, gotowe do pokazania w tooltipie. */
  reason: string;
}

export const DATA_QUALITY_LABEL: Record<DataQuality, string> = {
  GOOD: 'Dane pełne',
  PARTIAL: 'Dane niepełne',
  LOW_SAMPLE: 'Mała próba',
  MISSING_HISTORY: 'Brak historii',
  UNRELIABLE_JOIN: 'Niepewne łączenie danych',
  UNKNOWN: 'Jakość nieznana',
};

/** Kolejność od najlepszej do najgorszej — do agregacji „najsłabsze ogniwo”. */
const RANK: readonly DataQuality[] = ['GOOD', 'PARTIAL', 'LOW_SAMPLE', 'MISSING_HISTORY', 'UNRELIABLE_JOIN', 'UNKNOWN'];

/** Jakość zbioru = jakość jego najsłabszego elementu. Nigdy nie zawyżamy. */
export function worstQuality(values: readonly DataQuality[]): DataQuality {
  let worst: DataQuality = 'GOOD';
  for (const v of values) {
    if (RANK.indexOf(v) > RANK.indexOf(worst)) worst = v;
  }
  return worst;
}

export interface SampleAssessmentInput {
  /** Liczba obserwacji, na których liczona jest metryka (np. dni sprzedażowych). */
  sampleSize: number;
  /** Ile obserwacji oczekiwaliśmy (np. dni oczekiwanych z grafiku). */
  expectedSize?: number | null;
  /** Minimalna próba, poniżej której nie warto wyciągać wniosków. */
  minSample?: number;
}

/**
 * Ocena metryki okresowej. Świadomie prosta i deterministyczna — to jest bezpiecznik,
 * a nie model statystyczny. Progi są jawne, żeby dało się je zakwestionować.
 */
export function assessSample(input: SampleAssessmentInput): DataQualityAssessment {
  const { sampleSize, expectedSize = null, minSample = 5 } = input;

  if (!Number.isFinite(sampleSize) || sampleSize < 0) {
    return { quality: 'UNKNOWN', reason: 'Backend nie podał liczby obserwacji.' };
  }
  if (sampleSize === 0) {
    return { quality: 'MISSING_HISTORY', reason: 'W wybranym oknie nie ma żadnej obserwacji.' };
  }
  if (sampleSize < minSample) {
    return {
      quality: 'LOW_SAMPLE',
      reason: `Metryka policzona z ${sampleSize} obserwacji (próg ostrożności: ${minSample}).`,
    };
  }
  if (expectedSize != null && expectedSize > 0 && sampleSize < expectedSize) {
    const pct = Math.round((sampleSize / expectedSize) * 100);
    return {
      quality: 'PARTIAL',
      reason: `Dane pokrywają ${pct}% oczekiwanych obserwacji (${sampleSize} z ${expectedSize}).`,
    };
  }
  return { quality: 'GOOD', reason: `Metryka policzona z ${sampleSize} obserwacji.` };
}
